import { zodResolver } from "@hookform/resolvers/zod";
import { map } from "lodash";
import { Trash2Icon } from "lucide-react";
import { FC, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { UniqueTabColumnType } from "@/data/types";
import { useCMSStore } from "@/hooks/use-cms-store";

import { useCMSTabContext } from "../cms-tab-context";

type Props = {
  index: number;
  columnName?: string;
  columnType?: UniqueTabColumnType;
  isDownloadable?: boolean;
  allColumnNames: string[];
  hasLabel?: boolean;
};

const columnMappingFormSchema = z.object({
  column_name: z
    .string()
    .trim()
    .optional()
    .refine((val) => val && val.length > 0, {
      message: "Required",
    }),
  column_type: z.enum(UniqueTabColumnType),
  is_downloadable: z.boolean(),
});

const getLabelByColumnType = (columnType: UniqueTabColumnType) => {
  switch (columnType) {
    case UniqueTabColumnType.VIDEO:
      return "Video";
    case UniqueTabColumnType.IMAGE:
      return "Image";
    case UniqueTabColumnType.TEXT:
      return "Text";
    default:
      return "Unknown";
  }
};

const COLUMN_TYPES = [
  UniqueTabColumnType.TEXT,
  UniqueTabColumnType.IMAGE,
  UniqueTabColumnType.VIDEO,
];

const DOWNLOADABLE_COLUMN_TYPES = [
  UniqueTabColumnType.IMAGE,
  UniqueTabColumnType.VIDEO,
];

const isDownloadableColumnType = (columnType: UniqueTabColumnType) => {
  return DOWNLOADABLE_COLUMN_TYPES.includes(columnType);
};

export const ColumnMappingRow: FC<Props> = ({
  index,
  columnName,
  columnType,
  isDownloadable,
  allColumnNames,
  hasLabel = false,
}) => {
  const { tabIndex } = useCMSTabContext();
  const { onUpdateUniqueTabColumnMapping, onRemoveUniqueTabColumnMapping } =
    useCMSStore();

  const form = useForm({
    resolver: zodResolver(columnMappingFormSchema),
    defaultValues: {
      column_name: columnName,
      column_type: columnType,
      is_downloadable: isDownloadable ?? false,
    },
    mode: "onChange",
  });

  useEffect(() => {
    const unsubscribe = form.subscribe({
      formState: { values: true },
      callback: ({ values }) => {
        if (values.column_name && values.column_type) {
          onUpdateUniqueTabColumnMapping(tabIndex, index, {
            columnName: values.column_name,
            columnType: values.column_type,
            isDownloadable:
              isDownloadableColumnType(values.column_type) &&
              values.is_downloadable,
          });
        }
      },
    });

    return unsubscribe;
  }, [form, onUpdateUniqueTabColumnMapping, tabIndex, index]);

  const handleRemoveColumnMapping = useCallback(() => {
    onRemoveUniqueTabColumnMapping(tabIndex, index);
  }, [onRemoveUniqueTabColumnMapping, tabIndex, index]);

  return (
    <Form {...form}>
      <div className="flex items-start gap-4">
        <div className="w-12">
          {hasLabel && (
            <p className="text-sm font-medium leading-none mb-2">Number</p>
          )}
          <div className="flex items-center w-full size-9 text-muted-foreground font-mono text-sm pl-3">
            {index + 1}.
          </div>
        </div>

        <div className="flex flex-1 gap-4">
          <FormField
            control={form.control}
            name="column_name"
            render={({ field }) => (
              <FormItem className="flex-1">
                {hasLabel && <FormLabel>CSV Column</FormLabel>}
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  disabled={field.disabled}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select column" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {map(allColumnNames, (name) => (
                      <SelectItem key={name} value={name}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="column_type"
            render={({ field }) => (
              <FormItem className="flex-1">
                {hasLabel && <FormLabel>Type</FormLabel>}
                <Select
                  onValueChange={(value: UniqueTabColumnType) => {
                    field.onChange(value);

                    if (!isDownloadableColumnType(value)) {
                      form.setValue("is_downloadable", false);
                    }
                  }}
                  defaultValue={field.value}
                  disabled={field.disabled}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select content type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {COLUMN_TYPES.map((columnType) => (
                      <SelectItem key={columnType} value={columnType}>
                        {getLabelByColumnType(columnType)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="is_downloadable"
            disabled={!isDownloadableColumnType(form.getValues("column_type"))}
            render={({ field }) => (
              <FormItem className="w-24">
                {hasLabel && <FormLabel>Downloadable</FormLabel>}
                <FormControl className="min-h-9 flex flex-col justify-center">
                  <div>
                    <Switch
                      onCheckedChange={field.onChange}
                      checked={field.value}
                      disabled={field.disabled}
                      className="scale-150 ml-2"
                    />
                  </div>
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={handleRemoveColumnMapping}
          className="self-end"
        >
          <Trash2Icon className="size-4" />
        </Button>
      </div>
    </Form>
  );
};
